import React, { FC, Fragment } from 'react';

import { EmptyState } from '@sbu/components.emptystate';
import { Loader } from '@sbu/components.loader';
import { Motion } from '@sbu/components.motion';

import { type EntityTypeFieldsAndIndicator } from '../Records/useEntityTypeRecordFields';
import { FavoriteFieldProperty } from './FavoriteFieldProperty';
import css from './FavoriteFields.module.scss';
import { MatchedFavFieldType } from './parsers';

interface FavoriteFieldsProps {
  fields: MatchedFavFieldType | undefined;
  loading?: boolean;
  entityTypeFieldsAndIndicator?: EntityTypeFieldsAndIndicator;
}

export const FavoriteFields: FC<
  React.PropsWithChildren<FavoriteFieldsProps>
> = ({ fields, loading = false, entityTypeFieldsAndIndicator }) => {
  const entries = fields ? Object.entries(fields) : [];
  const isEmpty = !entries.length;
  const entityId = entityTypeFieldsAndIndicator?.entityIndicatorData?.id;

  const renderField = ([fieldName, values]) => {
    // Multiple fields can share the same field type (e.g., "_ip") but represent different
    // semantic fields (e.g., "srcDevice_ip" vs "dstDevice_ip"). Since entity values use
    // the shortened type format "<field.type>-<value>" (e.g., "_ip-109.168.1.1"), we need to
    // match against the actual entity value rather than just the field type to determine
    // if the field has an associated indicator.
    const fieldType = entityTypeFieldsAndIndicator?.entityTypeFields?.find(
      (field) => field.field === fieldName
    );
    const fieldHasIndicator =
      fieldType &&
      values.some(({ value }) => `${fieldType.type}-${value}` === entityId);

    return (
      <FavoriteFieldProperty
        className={css.field}
        fieldName={fieldName}
        values={values}
        entityIndicatorData={
          fieldHasIndicator
            ? entityTypeFieldsAndIndicator?.entityIndicatorData
            : undefined
        }
      />
    );
  };

  return (
    <Fragment>
      {loading && <Loader minHeight={18} size='large' />}
      {!loading && isEmpty && <EmptyState height='250px' />}
      {!loading && !isEmpty && (
        <div className={css.container}>
          <Motion>
            {entries.map(([fieldName, values]) => (
              <Motion key={`favorite-field-chip-${fieldName}`} type='item'>
                {renderField([fieldName, values])}
              </Motion>
            ))}
          </Motion>
        </div>
      )}
    </Fragment>
  );
};
